/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { format, subDays } from "date-fns";

import jsonExport from "jsonexport/dist";
import { fillDownRows } from "../../../utils/exportsUtils";
import { API, graphqlOperation } from "aws-amplify";
import {
  searchInventoryStorage,
  searchStorageShipments,
} from "../../../graphql/report-queries";
import { getAccount } from "../../../graphql/queries";

import { Box, Card, Grid, Paper, Tooltip, Typography } from "@material-ui/core";

import {
  ArrayField,
  Button,
  Datagrid,
  DateField,
  downloadCSV,
  FunctionField,
  ListContextProvider,
  NumberField,
  ReferenceField,
  required,
  SimpleForm,
  TextField,
  Title,
  TopToolbar,
  useList,
  useListContext,
  useNotify,
} from "react-admin";

import { AccountDropDown } from "../../../components/common/AccountDropDown";
import {
  CreateIcon,
  EditIcon,
  ExportIcon,
} from "../../../components/common/iconography";
import { BackButton } from "../../../components/common/BackButton";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { Pagination } from "../../../components/Pagination";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { HelpButton } from "../../../components/common/HelpButton";
import { BillingPeriodSelector } from "./BillingPeriodSelector";

import { fetchAllPages, logError } from "../../../utils/appUtils";
import {
  InboundStatus,
  InventoryStatus,
  MAX_INVENTORY_QUERY_LIMIT,
} from "../../../utils/constants";
import { getUnitName } from "../../../utils/loadTypeUtils";
import { deepRedirect, getBackPath } from "../../../utils/utils";
import {
  getLeasedZones,
  fetchChargeRules,
  filterPeriodicStorageAccounts,
  getPeriodicStorageFrequencies,
  isPeriodicStorageFrequency,
  isSyntheticBillingRow,
  processInventoryStorageReportData,
  trimLeasedZoneInventories,
} from "../../resources/accounts/chargeRules/ChargeRuleUtils";
import { ChargeEvent } from "../../resources/accounts/chargeRules/chargeRuleConstants";

// Runaway guard on the report's two paginated reads, not a page budget: at
// MAX_INVENTORY_QUERY_LIMIT rows a page this is 25,000 rows for one account
// and period. Hitting it throws (throwOnTruncation) rather than warns: this
// report's totals feed an invoice, so a partial read must fail the report
// instead of silently underbilling.
const MAX_STORAGE_REPORT_PAGES = 100;

const csvGenerate = (finalData, hasWeight) => {
  let reportForExport = [];

  // Billing UOM columns used for diagnostic purposes
  // const billingUomColumns = Array.from(
  //   new Set(
  //     finalData.flatMap((inventory) =>
  //       (inventory.billableItems || [])
  //         .map((item) => item?.billableUom || item?.appliedRule?.billingUOM)
  //         .filter(Boolean),
  //     ),
  //   ),
  // ).sort();

  // weight source fallback getter function
  const getWeight = (item, inventory) => {
    if (item.weight && item.weightUOM) {
      return item.inventoryUOM === item.weightUOM
        ? item.quantity // preferred = quantity onHand
        : inventory.weight; // fallback = inventory weight
    }
    return "";
  };

  reportForExport = finalData.map((inventory) => {
    const inventoryForExport = {
      // existing fields
      Received: new Date(inventory.receivedAt).toLocaleDateString(),
      "Product Code": inventory.productCode,
      "Product Name": inventory.productName,
      "Order No.": `${inventory.poNumber}`,
      "Car/Trl No.": `${inventory.railCarNumber}`,
      "Total Fee": inventory.totalFees,
      // inventory-level debug fields
      // "Inv ID": inventory.id,
      // "Inv Status": inventory.status,
      // "Inv Billable Qty": (inventory.billableItems || []).reduce(
      //   (sum, bi) => sum + (Number(bi?.quantity) || 0), 0,
      // ),
      // "Inv Total Stored Days": inventory.totalDaysStored,
      // "Inv Total Billable Days": inventory.billableDaysTotal,
      // "Inv Turn Days": inventory.turnDays,
      // "Inv Last Ship Date":
      //   inventory.depletedAt &&
      //   new Date(inventory.depletedAt).toLocaleDateString(),
      // rule-level debug fields
      // "Rule ID": rule.id,
      // "Rule Billing UOM": rule.billingUOM,
      // "Rule Free Days": rule.freeDays,
      // "Rule Free Days Policy": rule.freeDaysPolicy,
      // "Rule Frequency": rule.frequency,
      // "Rule Fee Accrual": rule.feeAccrual,
      // "Rule Carrier Type": rule.carrierType,
      // "Rule Min Charge": rule.minCharge,
      // "Rule Max Charge": rule.maxCharge,
    };

    if (inventory.billableItems?.length) {
      inventoryForExport.billable = inventory.billableItems.map((item) => {
        const lineRule = item?.appliedRule || {};
        const lineService = lineRule?.service || {};

        // Billing Qty by billing UOM columns for diagnostic purposes
        // const billingUOM = item.billableUom || lineRule?.billingUOM || "";
        // const billedQuantity = item.billedQuantity || "";
        // const quantityByBillingUom = billingUomColumns.reduce((accum, uom) => {
        //   accum[`Billable Quantity ${uom}`] =
        //     billingUOM === uom ? billedQuantity : "";
        //   return accum;
        // }, {});

        const lineItem = {
          Quantity: item.quantity,
          UOM: item.inventoryUOM,
          ...(hasWeight && {
            Weight: getWeight(item, inventory),
            "Weight UOM": item.weight && item.weightUOM ? item.weightUOM : "",
          }),
          Service: lineService?.name || "",
          Days: item.billableDays,
          Shipped: item.dateShipped
            ? new Date(item.dateShipped).toLocaleDateString()
            : "",
          Fee: item.fees,
          // per-line debug fields
          // ...quantityByBillingUom,
          // Area: item.areaId || "",
          // Zone: item.zoneId || "",
          // "Line Billable Days": item.billableDays,
        };
        return lineItem;
      });
    }

    return inventoryForExport;
  });

  return fillDownRows(reportForExport);
};

/**
 * downloadExporter
 * @param {Array} finalData - The data array to be converted to CSV.
 * @param {function} notify - Unused in this snippet, but kept for compatibility.
 */
const downloadExporter = (finalData, hasWeight, notify) => {
  const dateString = new Date().toLocaleDateString().replace(/\//g, "_");

  try {
    let reportForExport = csvGenerate(finalData, hasWeight);

    jsonExport(reportForExport, { rename: [] }, (err, csv) => {
      downloadCSV(csv, `storage_report_${dateString}`); // download as 'storage_report_date.csv` file
    });
  } catch (err) {
    logError("InventoryStorageReport:downloadExporter", err, notify);
  }
};

/**
 * fileExporter
 * @param {Array} finalData - The data array to be converted to CSV.
 * @returns {Promise<void|File>} - Returns a promise that resolves a File object.
 */
const fileExporter = async (finalData, hasWeight) => {
  let reportForExport = csvGenerate(finalData, hasWeight);
  const dateString = new Date().toLocaleDateString().replace(/\//g, "_");

  return new Promise((resolve, reject) => {
    jsonExport(reportForExport, { rename: [] }, (err, csv) => {
      if (err) return reject(err);
      const blob = new Blob([csv], { type: "text/csv" });
      const file = new File([blob], `storage_report_${dateString}.csv`, {
        type: "text/csv",
      });
      resolve(file);
    });
  });
};

const handleInvoiceCreate = async (
  account,
  finalData,
  hasWeight,
  startDate,
  endDate,
  history,
  notify,
) => {
  try {
    const reportFile = await fileExporter(finalData, hasWeight);

    deepRedirect(history, "/invoices/create", null, {
      event: ChargeEvent.STORAGE,
      accountId: account.id,
      serviceDate: new Date(endDate),
      dateRange: getReportDates(startDate, endDate),
      reportFile: reportFile,
      billingData: finalData, // Pass the inventory data with accrual information
    });
  } catch (err) {
    logError("InventoryStorageReport:handleInvoiceCreate", err, notify);
  }
};

const ListActions = (props) => {
  const history = useHistory();
  const notify = useNotify();
  const validateRequired = required();
  const {
    finalData,
    hasWeight,
    account,
    setAccount,
    storageRules,
    setStorageRules,
    frequency,
    setFrequency,
    startDate,
    endDate,
    handleBillingPeriodChange,
    getReportData,
    clearReportData,
    unitTotals,
    loading,
  } = props;

  const getAccountHelperText = () => {
    return account && !storageRules?.length
      ? "Account has no periodic storage billing rules."
      : false;
  };
  const handleAccountChange = async (value) => {
    try {
      const accountId = value?.id;
      if (accountId) {
        const response = await API.graphql(
          graphqlOperation(getAccount, { id: accountId }),
        );
        const accountData = response.data.getAccount;
        setAccount(accountData);

        // Fetch charge rules to get frequency and accrual settings
        try {
          const chargeRules = await fetchChargeRules(
            accountId,
            ChargeEvent.STORAGE,
            true,
          );
          const periodicChargeRules = chargeRules.filter((rule) =>
            isPeriodicStorageFrequency(rule?.frequency),
          );
          const periodicFrequencies =
            getPeriodicStorageFrequencies(periodicChargeRules);

          // Periodic report billing must ignore event-driven storage rules.
          // The billing period selector only makes sense for periodic frequencies.
          const ruleFrequency =
            periodicFrequencies.length === 1 ? periodicFrequencies[0] : "";

          setFrequency(ruleFrequency);
          setStorageRules(periodicChargeRules);
        } catch (ruleError) {
          // Clear report state if periodic storage rules cannot be loaded
          setFrequency("");
          setStorageRules([]);
          logError(
            "InventoryStorageReport:handleAccountChange:fetchChargeRules",
            ruleError,
            notify,
          );
        }
      } else {
        setAccount(null);
        setFrequency("");
        setStorageRules([]);
        clearReportData();
      }
    } catch (err) {
      logError("InventoryStorageReport:handleAccountChange", err, notify);
    } finally {
      clearReportData();
    }
  };

  const isRunDisabled = () => {
    return !account?.id || !frequency || !storageRules?.length || loading;
  };

  const hasMissingRates = () => {
    if (!unitTotals) return false;
    return Object.values(unitTotals).some((value) => typeof value === "string");
  };

  const isInvoiceDisabled = () => {
    return finalData?.length === 0 || hasMissingRates();
  };

  return (
    <Box mt={-4}>
      <TopToolbar>
        <Grid container spacing={1}>
          <Grid item xs={12} md={4}>
            <AccountDropDown
              source="accountId"
              label="Account"
              onAccountChange={(value) => handleAccountChange(value)}
              validate={validateRequired}
              allowEmpty={false}
              fullWidth
              helperText={getAccountHelperText()}
              filterAccounts={filterPeriodicStorageAccounts}
            />
          </Grid>
          <Grid item xs={12} md={6} xl={3}>
            <BillingPeriodSelector
              label="Billing Period"
              frequency={frequency}
              onPeriodChange={handleBillingPeriodChange}
              disabled={loading || !account?.id}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box mt={1}>
              <Button
                label="RUN"
                variant="contained"
                color="default"
                size="large"
                onClick={getReportData}
                disabled={isRunDisabled()}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Toolbar Section */}
        <HelpButton />
        <Box mr={1}>
          {hasMissingRates() ? (
            <Tooltip title="Has Missing Rates" arrow>
              <span>
                <Button label="Invoice" disabled>
                  <CreateIcon />
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              label="Invoice"
              disabled={isInvoiceDisabled()}
              onClick={() =>
                handleInvoiceCreate(
                  account,
                  finalData,
                  hasWeight,
                  startDate,
                  endDate,
                  history,
                  notify,
                )
              }
            >
              <CreateIcon />
            </Button>
          )}
        </Box>
        <Box mr={1}>
          <Button
            label="Export"
            disabled={finalData?.length === 0}
            onClick={() => downloadExporter(finalData, hasWeight, notify)}
          >
            <ExportIcon />
          </Button>
        </Box>
        <BackButton basePath="/reports" />
      </TopToolbar>
    </Box>
  );
};

const getReportDates = (startDate, endDate) => {
  if (startDate && endDate) {
    const endDateInclusive = subDays(new Date(`${endDate}T00:00:00`), 1);
    const dateRange = `${new Date(
      `${startDate}T00:00:00`,
    ).toLocaleDateString()} - ${endDateInclusive.toLocaleDateString()}`;

    return dateRange;
  }
  return "";
};

// /reportInventoryStorage
// Get all inventories that were in storage during last billing cycle (last month)
export const InventoryStorageReport = (props) => {
  const notify = useNotify();

  // filter state vars
  const [account, setAccount] = useState(null);
  const [storageRules, setStorageRules] = useState([]);
  const [frequency, setFrequency] = useState("");
  // report state vars
  const [finalData, setFinalData] = useState([]);
  const [unitTotals, setUnitTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasWeight, setHasWeight] = useState(false);

  // Get the first & last day of last month
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const reloadYardId = window.appUser?.reloadYardId
    ? window.appUser.reloadYardId
    : props.history?.location?.state?.reloadYardId;

  useEffect(() => {
    if (!frequency) {
      setStartDate("");
      setEndDate("");
    }
  }, [frequency]);

  const clearReportData = () => {
    setFinalData([]);
    setUnitTotals({});
    setHasWeight(false);
  };

  const handleBillingPeriodChange = useCallback(({ startDate, endDate }) => {
    setStartDate(startDate);
    setEndDate(endDate);
    clearReportData();
  }, []);

  const getReportData = async () => {
    try {
      setLoading(true);
      clearReportData();

      // Use the smallest freeDays so you never exclude billable items.
      // This ensures we capture all inventory that could potentially be billable
      // regardless of which specific rule applies to each item
      // Rules saved without freeDays have none, so no finite value means 0
      // (Math.min of nothing is Infinity, an invalid date below).
      const finiteFreeDays = storageRules
        .map((r) => Number(r.freeDays))
        .filter(Number.isFinite);
      const minFreeDays = finiteFreeDays.length
        ? Math.min(...finiteFreeDays)
        : 0;

      // Period boundaries are provided by BillingPeriodSelector.
      // Inventory must be received before this date to be eligible for billing
      // This is calculated by subtracting the min freeDays from the report end date
      // This ensures we don't bill inventory that's still within its free period
      //
      // One day later than the local-day math needs: receivedAt is stored in
      // UTC, so an evening receipt west of UTC already reads as the next day
      // against this local date string. Rows let through early are dropped by
      // the free-days check in ChargeRuleUtils.
      const periodEnd = new Date(`${endDate}T00:00:00`);
      const billingEligibilityStartDate = format(
        subDays(periodEnd, minFreeDays - 1),
        "yyyy-MM-dd",
      );

      // console.log("billingEligibilityStartDate", billingEligibilityStartDate);
      // console.log("startDate", startDate);
      // console.log("endDate", endDate);

      // - STORED: receivedAt < cutoff (strict) to avoid off-by-one
      // - SHIPPED: within period (startDate..endDate), turnDays > minFreeDays - 2
      //   and receivedAt < cutoff (same rationale)
      const inventoryVariables = {
        // must use deterministic sort to avoid duplicate records in the export
        // cannot use receivedAt because it is not unique
        sort: { field: "id", direction: "asc" },
        filter: {
          // id: { eq: "90fc1d14-a2a7-453d-a577-8a1e82f06f94" },
          // reloadYardCommodityId: {
          //   eq: "31aa7feb-67a1-4ec9-b77c-8f6ae60f6b63",
          // },
          // reloadYardCommodityId: {
          //   eq: "a9e52947-a348-4dc3-a44f-b1e611b8eaec",
          // },
          reloadYardId: { eq: reloadYardId },
          accountId: { eq: account.id },
          or: [
            {
              // Currently stored inventory that was received before the billing cutoff
              // These items are still in storage and may be billable
              and: [
                { status: { eq: InventoryStatus.STORED } },
                { receivedAt: { lt: billingEligibilityStartDate } },
              ],
            },
            {
              // Shipped inventory that was received before the billing cutoff
              // AND had turn days greater than the free days threshold
              //
              // This keys on the product's depletedAt. A car billed per
              // railcar whose stock was depleted before the period, but which
              // sat on the track into it, is not returned here: OpenSearch
              // cannot filter on nested inboundShipment fields. The
              // searchStorageShipments read below picks that car up from the
              // shipment record instead.
              and: [
                { status: { eq: InventoryStatus.SHIPPED } },
                { receivedAt: { lt: billingEligibilityStartDate } },
                // required to eliminate free days. turnDays counts whole
                // 24-hour periods; storage counts calendar days with both
                // ends included, up to two more, so the bar sits two lower.
                { turnDays: { gt: Number(minFreeDays) - 2 } },
                { depletedAt: { gte: startDate } },
                // Shipped after endDate is only billable through endDate
                // do not add { depletedAt: { lt: endDate } }
                // don’t exclude records that shipped after the billing period end
                // because they can still be billable up to the end boundary.
              ],
            },
          ],
        },
      };

      // Vehicle classes a per-car (carrierType) storage rule can bill. Cars
      // are billed per railcar off the InboundShipment itself: an empty SIT
      // tally sheet still bills, and a tracked car whose stock was depleted
      // before the period still bills for the days it sat on the track into
      // it. Without a per-car rule no car can bill, so the read is skipped.
      const perCarCarrierTypes = [
        ...new Set(
          storageRules.map((rule) => rule?.carrierType).filter(Boolean),
        ),
      ];
      const hasPerCarStorageRule = perCarCarrierTypes.length > 0;

      const shipmentVariables = {
        sort: { field: "id", direction: "asc" },
        filter: {
          reloadYardId: { eq: reloadYardId },
          accountId: { eq: account.id },
          // shipments flagged never-to-invoice (stock imports, brokered
          // accounts) must not generate storage charges. `ne` keeps rows
          // where the flag is false or was never written.
          skipInvoice: { ne: true },
          placedDateTime: { lt: billingEligibilityStartDate },
          and: [
            {
              or: perCarCarrierTypes.map((carrierType) => ({
                carrierType: { eq: carrierType },
              })),
            },
            {
              or: [
                {
                  // Still parked on a track — billable through the period
                  // end. Collecting a car writes collectedDateTime without
                  // touching its status, so a car pulled without a release
                  // stays PLACED for good: the pull date, not the status,
                  // decides whether it is still here or left inside the
                  // period.
                  and: [
                    {
                      or: [
                        { status: { eq: InboundStatus.PLACED } },
                        { status: { eq: InboundStatus.SIT_PLACED } },
                      ],
                    },
                    {
                      or: [
                        { collectedDateTime: { exists: false } },
                        { collectedDateTime: { gte: startDate } },
                      ],
                    },
                  ],
                },
                {
                  // Left during or after the period — billable through the
                  // pull (collectedDateTime), or the release date when the car
                  // was never marked collected, or the period end, whichever
                  // is first. Both dates are tested because a car released
                  // before the period can still sit on the track and bill
                  // until it is pulled.
                  and: [
                    { status: { eq: InboundStatus.RELEASED } },
                    {
                      or: [
                        { collectedDateTime: { gte: startDate } },
                        { releaseDateTime: { gte: startDate } },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      };

      // The two reads answer different questions and neither feeds the other,
      // so the report waits for the slower one rather than for their sum.
      const [inventories, storageShipments] = await Promise.all([
        fetchAllPages({
          query: searchInventoryStorage,
          variables: inventoryVariables,
          responseKey: "searchInventorys",
          pageLimit: MAX_INVENTORY_QUERY_LIMIT,
          maxPages: MAX_STORAGE_REPORT_PAGES,
          label: "storage report inventories",
          throwOnTruncation: true,
        }),
        hasPerCarStorageRule
          ? fetchAllPages({
              query: searchStorageShipments,
              variables: shipmentVariables,
              responseKey: "searchInboundShipments",
              pageLimit: MAX_INVENTORY_QUERY_LIMIT,
              maxPages: MAX_STORAGE_REPORT_PAGES,
              label: "storage report per-car shipments",
              throwOnTruncation: true,
            })
          : [],
      ]);

      const trimmedInventories = trimLeasedZoneInventories(
        inventories,
        account.id,
      );
      const leasedZones = getLeasedZones(account.id);

      // Sort by receivedAt (asc), with id as a stable tie-breaker
      const sortedInventories = trimmedInventories.sort(
        (a, b) =>
          new Date(a.receivedAt) - new Date(b.receivedAt) ||
          a.id.localeCompare(b.id),
      );

      // Process the inventory data using charge rules to calculate fees
      // This will apply the appropriate charge rule to each inventory item
      // and calculate the billable amounts based on the specific rule's settings
      processInventoryStorageReportData({
        inventories: sortedInventories,
        leasedZones,
        storageShipments,
        storageRules,
        options: {
          startDate,
          endDate,
        },
        setFinalData,
        setUnitTotals,
        setHasWeight,
      });
    } catch (err) {
      logError("InventoryStorageReport:getReportData", err, notify);
      clearReportData();
    } finally {
      setLoading(false);
    }
  };

  const fixedFilter = ""; // {"lowInventoryAlert": true};
  const defaultSort = { field: "receivedAt", order: "ASC" };
  const defaultPerPage = 25;

  const listContext = useList({
    ...props,
    data: finalData,
    ids: finalData.map(({ id }) => id),
    loading,
    loaded: !loading,
    filter: fixedFilter,
    sort: defaultSort,
    perPage: defaultPerPage,
  });

  return (
    <SimpleForm toolbar={false}>
      <ListContextProvider value={listContext}>
        <Title title={<ForceTitle title="Storage Report" />} />
        <ListActions
          {...props}
          finalData={finalData}
          hasWeight={hasWeight}
          account={account}
          setAccount={setAccount}
          frequency={frequency}
          setFrequency={setFrequency}
          storageRules={storageRules}
          setStorageRules={setStorageRules}
          startDate={startDate}
          endDate={endDate}
          handleBillingPeriodChange={handleBillingPeriodChange}
          getReportData={getReportData}
          clearReportData={clearReportData}
          unitTotals={unitTotals}
          loading={loading}
        />
        <Card>
          <ReportGrid
            account={account}
            startDate={startDate}
            endDate={endDate}
            hasWeight={hasWeight}
            history={props.history}
            backPath={getBackPath(props)}
          />
          <PaginationToolbar unitTotals={unitTotals} />
        </Card>
      </ListContextProvider>
    </SimpleForm>
  );
};

const ReportGrid = ({
  account,
  startDate,
  endDate,
  hasWeight,
  history,
  backPath,
  ...props
}) => {
  const listContext = useListContext(props);
  const label = getReportDates(startDate, endDate);

  if (!account?.id || !startDate) return null;
  return (
    <CustomizableDatagrid
      {...listContext}
      label={label}
      rowClick="expand"
      expand={<BillableItemsDataGrid hasWeight={hasWeight} />}
      basePath="/inventories"
      resource="inventories"
      storageProp={`InventoryStorageReport_${account?.id}_large`}
      defaultColumns={[
        "receivedAt",
        "productName",
        "poNumber",
        "railCarNumber",
        "totalFees",
      ]}
    >
      {[
        <DateField source="receivedAt" label="Reveived" showTime={false} />,
        <TextField source="productCode" label="Product Code" />,
        <TextField source="productName" label="Product Name" />,
        <TextField source="poNumber" label="PO Number" />,
        <TextField source="railCarNumber" label="Car/Trl No." />,
        <NumberField source="totalDaysStored" label="Total Stored Days" />,
        <NumberField source="billableDaysTotal" label="Total Billable Days" />,
        <NumberField
          source="totalFees"
          label="Total Fee"
          options={{
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }}
        />,
        <FunctionField
          label="Edit"
          render={(record) =>
            // synthetic rows (leased zones, SIT cars) have no inventory record to edit
            isSyntheticBillingRow(record) ? null : (
              // deepRedirect instead of EditButton's plain link: it appends the
              // report path to the breadcrumb so the inventory record's Back
              // button returns here rather than to the inventory list
              <Button
                label="Edit"
                onClick={() =>
                  deepRedirect(
                    history,
                    `/inventories/${record.id}`,
                    backPath,
                    {},
                  )
                }
              >
                <EditIcon />
              </Button>
            )
          }
        />,
      ]}
    </CustomizableDatagrid>
  );
};

export const BillableItemsDataGrid = ({ record, hasWeight, ...props }) => {
  return (
    <Paper variant="outlined">
      <ArrayField source="billableItems" record={record} label="Billable Items">
        <Datagrid>
          <NumberField source="quantity" label="Quantity" />
          <FunctionField
            source="inventoryUOM"
            label="UOM"
            render={(record) => getUnitName(record.inventoryUOM)}
          />
          {hasWeight && <NumberField source="weight" label="Weight" />}
          {hasWeight && (
            <FunctionField
              source="weightUOM"
              label="Weight UOM"
              render={(record) => getUnitName(record.weightUOM)}
            />
          )}
          <ReferenceField
            source="areaId"
            reference="areas"
            label="Area"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
          {/* <ReferenceField
            source="zoneId"
            reference="zones"
            label="Zone"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField> */}
          <FunctionField
            label="Service"
            render={(item) => item?.appliedRule?.service?.name ?? ""}
          />
          <FunctionField
            label="Free Days"
            render={(item) => {
              const rule = item?.appliedRule;
              if (!rule) return "";

              // If rule is configured as "free until end of month", show EOM
              if (rule.freeDaysPolicy === "END_OF_MONTH") {
                return "EOM";
              }

              return rule.freeDays ?? "";
            }}
          />
          <NumberField source="billableDays" label="Billable Days" />
          <DateField source="dateShipped" label="Shipped" showTime={false} />
          <NumberField
            source="fees"
            label="Fee"
            options={{
              style: "currency",
              currency: "USD",
              maximumFractionDigits: 2,
            }}
          />
        </Datagrid>
      </ArrayField>
    </Paper>
  );
};

const PaginationToolbar = ({ unitTotals, ...props }) => {
  const listContext = useListContext(props);
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });
  const totalElements = [];
  const totalsArray = Object.entries(unitTotals);
  let numericTotal = 0;

  useEffect(() => {
    if (listContext?.perPage) listContext.setPage(1);
  }, [listContext?.perPage]);

  // Process each UOM subtotal
  for (const [uom, subtotal] of totalsArray) {
    // Display each UOM with its subtotal
    totalElements.push(
      <Typography key={uom}>
        {uom}:{" "}
        {typeof subtotal === "string" ? subtotal : formatter.format(subtotal)}
      </Typography>,
    );

    // Only add numeric subtotals to the grand total
    if (typeof subtotal === "number" && !isNaN(subtotal)) {
      numericTotal += subtotal;
    }
  }

  // Add final total line (only numeric subtotals)
  totalElements.push(
    <Typography key="Total" style={{ fontWeight: "bold" }}>
      Total: {formatter.format(numericTotal)}
    </Typography>,
  );

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Grid item xs={4}>
        <Box pl={2}>{totalElements.length > 0 ? totalElements : <></>}</Box>
      </Grid>
      <Grid item xs={8}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};
